import { DialogContent, DialogOverlay } from "@reach/dialog";
import { animated, useSpring } from "@react-spring/web";
import classNames from "classnames";
import { AttributePanelMultiAttribute } from "components/AttributePanel/AttributePanelMultiAttribute";
import Clickable from "components/Clickable";
import { LoadingOverlay } from "components/Loading/LoadingOverlay";
import { getSelectedFinishImagePath } from "components/VisualConfigurator";
import { useIsMobile } from "helpers/utils";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { CloudinaryImage } from "react-tiny-cloudinary";

import {
    IVisualizerPanelOptionProps,
    IVisualizerPanelRulesProps,
    IVisualizerStepProps,
} from ".";
import CaretIcon from "../../public/svg/caret.svg";

import styles from "./visualizer.module.scss";

interface IVisualizerPanelProps {
    rule: IVisualizerPanelRulesProps;
    ruleArray: Array<IVisualizerPanelRulesProps>;
    currentStep: IVisualizerStepProps;
    nextStep: IVisualizerStepProps | null;
    onNextStep: (step: IVisualizerStepProps | null) => void;
    mobileRender: JSX.Element;
    setMobileRender: (show: boolean) => void;
    onOpen: (open: boolean) => void;
    dataLoading: boolean;
}

export const VisualizerPanel = ({
    rule,
    ruleArray,
    currentStep,
    nextStep,
    onNextStep,
    mobileRender,
    setMobileRender,
    onOpen,
    dataLoading,
}: IVisualizerPanelProps) => {
    const router = useRouter();

    const [showVisualizerModal, setShowVisualizerModal] = useState(false);
    const [invalidOption, setInvalidOption] = useState("");
    const animationStyle = useSpring({
        translateX: showVisualizerModal ? "0" : "100%",
    });

    const queryOptionID = router.query[`rule-${rule.id}`];
    const queryOption = rule.options.find(
        (option) => option.id.toString() === queryOptionID,
    );
    let queryFilterID: string | string[] | undefined;
    let queryFilter: IVisualizerPanelOptionProps | undefined;
    if (rule.filter_rule) {
        queryFilterID = router.query[`rule-${rule.filter_rule.id}`];
        queryFilter = rule.filter_rule.options.find(
            (option) => option.id.toString() === queryFilterID,
        );
    }

    let currentLabel;
    if (queryOption && queryFilter) {
        currentLabel = `${queryOption.option} in ${queryFilter.option}`;
    } else {
        currentLabel = `Choose a ${rule.title}`;
    }

    const currentOptionIndex = rule.options.findIndex((r) => r === queryOption);
    const currentFilterIndex =
        rule.filter_rule?.options.findIndex((fr) => fr === queryFilter) ?? -1;

    useEffect(() => {
        if (queryOption) {
            setInvalidOption("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryOption?.id]);

    const noOptionOrFilter =
        currentOptionIndex === -1 || currentFilterIndex === -1;

    const onClosePanel = () => {
        onOpen(false);
        setShowVisualizerModal(false);
    };

    const buildLabelThumbnail = () => {
        if (noOptionOrFilter) {
            return null;
        }

        let finishPath = null;
        if (rule.filter_rule) {
            finishPath = getSelectedFinishImagePath(
                [rule.filter_rule?.options[currentFilterIndex]],
                rule.options[currentOptionIndex].finish_images,
            );

            if (!finishPath) {
                finishPath =
                    rule.options[currentOptionIndex].primary_image?.image;
            }
        }

        return (
            <CloudinaryImage
                type={finishPath ? "fetch" : "upload"}
                cldId={finishPath ? finishPath : undefined}
                cldSrcSet={[80, 160]}
                aspectRatio="1:1"
            >
                <img alt="" sizes="160px" />
            </CloudinaryImage>
        );
    };

    return (
        <>
            <div>{`Choose a ${rule.title}`}</div>
            {invalidOption && (
                <div className={styles.invalidOption}>{invalidOption}</div>
            )}
            <Clickable
                className={classNames({
                    [styles.multiattributeTrigger]: true,
                    [styles.multiattributeTriggerInvalid]: noOptionOrFilter,
                })}
                onClick={() => {
                    onOpen(true);
                    setShowVisualizerModal(true);
                }}
            >
                {buildLabelThumbnail()}
                <span
                    className={classNames({
                        [styles.invalidLabel]: noOptionOrFilter,
                    })}
                >
                    {currentLabel}
                </span>
                <CaretIcon />
            </Clickable>
            <DialogOverlay
                isOpen={showVisualizerModal}
                onDismiss={dataLoading ? () => {} : onClosePanel}
                style={{ background: "rgba(0,0,0,0)" }}
            >
                {dataLoading && (
                    <LoadingOverlay
                        extraClass={styles.panelLoading}
                        visuallyHiddenText="Visualizer data is loading…"
                    />
                )}
                <DialogContent
                    aria-label={`Visualizer Panel – ${currentStep.title} / ${rule.title}`}
                    className={classNames({
                        [styles.multiattributePanel]: true,
                        [styles.multiattributePanelDisabled]: dataLoading,
                    })}
                >
                    {useIsMobile() && mobileRender}
                    <animated.div
                        style={animationStyle}
                        className={styles.panelContent}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <AttributePanelMultiAttribute
                            rule={rule}
                            ruleArray={ruleArray}
                            currentStep={currentStep}
                            nextStep={nextStep}
                            onNextStep={() => {
                                onOpen(false);
                                setShowVisualizerModal(false);
                                onNextStep(nextStep);
                            }}
                            selectedOption={rule.options[currentOptionIndex]}
                            selectedFilter={
                                rule.filter_rule?.options[currentFilterIndex]
                            }
                            onClosePanel={onClosePanel}
                            invalidOption={invalidOption}
                            onInvalidOption={(option: string) => {
                                setMobileRender(false);
                                setInvalidOption(option);
                            }}
                        />
                    </animated.div>
                </DialogContent>
            </DialogOverlay>
        </>
    );
};
